import { Trans, useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import type { PendingGroupSubscriptionInvite } from '~/services/api/subscription/listPendingGroupSubscriptionInvites.server';

interface Props {
  invites?: PendingGroupSubscriptionInvite[] | null;
}

/**
 * Section in the General/Billing tab that lists pending group invites for the
 * signed-in subscriber. The Accept CTA navigates to /accept-group-invite?token=...
 * rather than POSTing inline — the landing page already implements the four-state
 * machine (accept_ready / email_mismatch / already_accepted / invalid_token) and
 * enforces the email-mismatch guard. Inline POST would have to re-derive all of
 * that, and skipping email-mismatch would let a subscriber accept an invite
 * meant for a different account on the same browser.
 */
export const PendingGroupInvites = ({ invites }: Props) => {
  const { t } = useTranslation();

  if (!invites || invites.length === 0) return null;

  return (
    <section className="space-y-4">
      <header className="space-y-1">
        <h2 className="text-lg font-semibold text-gray-900">
          {t('accept_group_invite.pending_invites.section_title')}
        </h2>
        <p className="text-sm text-gray-600">{t('accept_group_invite.pending_invites.section_description')}</p>
      </header>
      <ul className="space-y-3">
        {invites.map((invite) => (
          <li
            key={invite.token}
            className="flex flex-col gap-4 p-4 bg-white border border-gray-200 rounded-lg shadow-sm sm:flex-row sm:items-center sm:justify-between"
          >
            <div className="space-y-1">
              <p className="text-base font-semibold text-gray-900">{invite.group_subscription.group_name}</p>
              <p className="text-sm text-gray-700">{invite.tier.name}</p>
              <p className="text-sm text-gray-500">
                <Trans
                  i18nKey="accept_group_invite.pending_invites.card_invited_by"
                  values={{ adminEmail: invite.group_subscription.admin_email }}
                  // `key` lives on the template element react-i18next clones per
                  // render. Without it, React's list-warning fires when this
                  // Trans renders inside a `.map()` — siblings produced by the
                  // tagged interpolation look like a keyless list to the
                  // reconciler.
                  components={{ strong: <strong key="strong" /> }}
                />
              </p>
            </div>
            <Link
              to={`/accept-group-invite?token=${encodeURIComponent(invite.token)}`}
              className="self-start px-4 py-2 text-sm font-medium bg-white border rounded-md shadow-sm whitespace-nowrap border-wt-primary text-wt-primary hover:bg-gray-50 sm:self-auto"
            >
              {t('accept_group_invite.pending_invites.card_cta')}
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
};
